import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { GetScheduleFillRateResponse } from '@classpass/protobuf-definitions/lib/web/models/partners/schedule';

import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { VENUE_FEATURE_PROBE_HTTP_CONFIG } from 'helpers/session-auth-http';
import { selectActiveVenueId } from 'selectors/general';
import { handler as fetchRosterEntity } from 'store/saga/entities/roster';
import { handler as fetchScheduleInsightsEntity } from 'store/saga/entities/scheduleInsights';

export const scheduleDetailsKeys = {
  all: ['scheduleDetails'],
  classRating: (venueId, classId) => [...scheduleDetailsKeys.all, 'classRating', venueId, classId],
  fillRate: (venueId, scheduleId) => [...scheduleDetailsKeys.all, 'fillRate', venueId, scheduleId],
  insights: (venueId, scheduleId) => [...scheduleDetailsKeys.all, 'insights', venueId, scheduleId],
  roster: (venueId, scheduleId) => [...scheduleDetailsKeys.all, 'roster', venueId, scheduleId],
};

export const SCHEDULE_DETAILS_STALE_TIME_MS = 5 * 60 * 1000;

// Venue-scoped reads opt out of the session-auth redirect so a mismatched venue/schedule pair
// cannot be treated as an expired session and log the partner out.
const detailReadsHttpClient = {
  get: (url, config) => httpClient.get(url, { ...VENUE_FEATURE_PROBE_HTTP_CONFIG, ...config }),
};

export async function fetchClassRatingStats({ venueId, classId }) {
  try {
    const response = await httpClient.get(URLS.ratings.statistics(venueId, { classId }), {
      ignoreInternalError: true,
    });
    return transformKeysToCamelCase(response?.data)?.averageRatings?.overTimeWindow || null;
  } catch (error) {
    reportError('Failed to fetch class rating statistics', { error, venueId, classId });
    throw error;
  }
}

export function useClassRatingStats(classId, options = {}) {
  const venueId = useSelector(selectActiveVenueId);

  return useQuery({
    queryKey: scheduleDetailsKeys.classRating(venueId, classId),
    queryFn: () => fetchClassRatingStats({ venueId, classId }),
    enabled: Boolean(venueId && classId),
    retry: 1,
    staleTime: SCHEDULE_DETAILS_STALE_TIME_MS,
    ...options,
  });
}

export async function fetchScheduleInsights({ venueId, scheduleId }) {
  try {
    return await fetchScheduleInsightsEntity({ httpClient: detailReadsHttpClient }, venueId, scheduleId);
  } catch (error) {
    reportError('Failed to fetch schedule insights', { error, venueId, scheduleId });
    throw error;
  }
}

export function useScheduleInsights(scheduleId, options = {}) {
  const venueId = useSelector(selectActiveVenueId);

  return useQuery({
    queryKey: scheduleDetailsKeys.insights(venueId, scheduleId),
    queryFn: () => fetchScheduleInsights({ venueId, scheduleId }),
    enabled: Boolean(venueId && scheduleId),
    retry: 1,
    staleTime: SCHEDULE_DETAILS_STALE_TIME_MS,
    ...options,
  });
}

export async function fetchScheduleFillRate({ venueId, scheduleId }) {
  try {
    const response = await detailReadsHttpClient.get(URLS.schedule.fillRate(venueId, scheduleId));
    return transformKeysToCamelCase(GetScheduleFillRateResponse.fromJSON(response?.data || {}).data) || {};
  } catch (error) {
    reportError('Failed to fetch schedule fill rate', { error, venueId, scheduleId });
    throw error;
  }
}

export function useScheduleFillRate(scheduleId, options = {}) {
  const venueId = useSelector(selectActiveVenueId);

  return useQuery({
    queryKey: scheduleDetailsKeys.fillRate(venueId, scheduleId),
    queryFn: () => fetchScheduleFillRate({ venueId, scheduleId }),
    enabled: Boolean(venueId && scheduleId),
    retry: 1,
    staleTime: SCHEDULE_DETAILS_STALE_TIME_MS,
    ...options,
  });
}

export async function fetchScheduleRoster({ venueId, scheduleId }) {
  try {
    return await fetchRosterEntity({ httpClient: detailReadsHttpClient }, venueId, scheduleId);
  } catch (error) {
    reportError('Failed to fetch schedule roster', { error, venueId, scheduleId });
    throw error;
  }
}

export function useScheduleRoster(scheduleId, options = {}) {
  const venueId = useSelector(selectActiveVenueId);

  return useQuery({
    queryKey: scheduleDetailsKeys.roster(venueId, scheduleId),
    queryFn: () => fetchScheduleRoster({ venueId, scheduleId }),
    enabled: Boolean(venueId && scheduleId),
    retry: 1,
    staleTime: SCHEDULE_DETAILS_STALE_TIME_MS,
    ...options,
  });
}
